import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Pencil, Trash2, Upload, Download } from 'lucide-react';
import toast from 'react-hot-toast';

import { LoadingLogo } from '@/components/shared/LoadingLogo';
import { ProductStatusBadge } from '@/components/shared/ProductStatusBadge';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { PageHeader } from '@/components/shared/PageHeader';
import { TableCard } from '@/components/shared/TableCard';
import { TablePagination } from '@/components/shared/TablePagination';
import { ConfirmationDialog } from '@/components/shared/ConfirmationDialog';
import { TableActionsMenu, type TableAction } from '@/components/shared/TableActionsMenu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/hooks/useAuth';
import { useIndustryTranslation } from '@/hooks/useIndustryTranslation';
import { usePaginationFromUrl } from '@/hooks/usePaginationFromUrl';
import { useTenantSwitcher } from '@/contexts/TenantSwitcherContext';
import {
  listItemsPaginated,
  createItem,
  createBulkByCategory,
  deleteItem,
  type CreateItemPayload,
} from '@/services/itemService';
import { listBranches } from '@/services/branchService';
import { listCategories, getNextProductSuggestion } from '@/services/categoryService';
import { listShelves } from '@/services/shelfService';
import {
  listCustomFieldDefinitions,
  upsertItemCustomFieldValues,
} from '@/services/customFieldService';
import { getItemDetectionStatus } from '@/utils/detectionStatus';
import type {
  Item,
  Branch,
  Category,
  CustomFieldDefinition,
  Shelf,
} from '@packages/shared-types';

const itemFormSchema = z.object({
  name: z.string().min(1, 'pages.items.form.nameRequired'),
  sku: z.string().min(1, 'pages.items.form.skuRequired'),
  rfidTagId: z.string().optional(),
  description: z.string().optional(),
  categoryId: z.string().uuid().optional().or(z.literal('')),
  branchId: z.string().min(1, 'pages.items.form.branchRequired'),
  currentShelfId: z.string().uuid().optional().or(z.literal('')),
});

type ItemFormValues = z.infer<typeof itemFormSchema>;

function formatShelfLocationLabel(shelf: Shelf): string {
  const parts = [shelf.zone, shelf.aisle, shelf.module, shelf.row, shelf.cell].filter(
    (v): v is string => v != null && v !== '',
  );
  return parts.length > 0 ? `${shelf.name} — ${parts.join(' / ')}` : shelf.name;
}

interface ParsedCsvRow {
  name: string;
  sku: string;
  rfidTagId: string;
  description?: string;
  categoryId?: string;
  branchId: string;
  currentShelfId?: string;
  rowNumber: number;
  errors?: string[];
}

// Simple CSV parser
function parseCSV(csvText: string): ParsedCsvRow[] {
  const lines = csvText.split('\n').filter((line) => line.trim());
  if (lines.length < 2) {
    throw new Error('CSV must have at least a header row and one data row');
  }

  const headerLine = lines[0];
  const headers = parseCSVLine(headerLine).map((h) => h.toLowerCase().trim());

  const requiredColumns = ['name', 'branchid'];
  const missingColumns = requiredColumns.filter(
    (col) => !headers.includes(col),
  );

  if (missingColumns.length > 0) {
    throw new Error(
      `Missing required columns: ${missingColumns.join(', ')}`,
    );
  }

  const nameIdx = headers.indexOf('name');
  const skuIdx = headers.indexOf('sku');
  const rfidTagIdIdx = headers.indexOf('rfidtagid');
  const descriptionIdx = headers.indexOf('description');
  const categoryIdIdx = headers.indexOf('categoryid');
  const branchIdIdx = headers.indexOf('branchid');
  const currentShelfIdIdx = headers.indexOf('currentshelfid');

  const rows: ParsedCsvRow[] = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) continue;

    const values = parseCSVLine(line);
    const rowNumber = i + 1;

    if (values.length !== headers.length) {
      rows.push({
        name: '',
        sku: '',
        rfidTagId: '',
        branchId: '',
        rowNumber,
        errors: [`Row ${rowNumber}: Column count mismatch`],
      });
      continue;
    }

    const name = values[nameIdx]?.trim() || '';
    const sku = skuIdx >= 0 ? values[skuIdx]?.trim() || '' : '';
    const rfidTagId = rfidTagIdIdx >= 0 ? values[rfidTagIdIdx]?.trim() || '' : '';
    const description = values[descriptionIdx]?.trim() || undefined;
    const categoryId = values[categoryIdIdx]?.trim() || undefined;
    const branchId = values[branchIdIdx]?.trim() || '';
    const currentShelfId = values[currentShelfIdIdx]?.trim() || undefined;

    const errors: string[] = [];
    if (!name) errors.push('Name is required');
    if (!branchId) errors.push('Branch ID is required');

    rows.push({
      name,
      sku,
      rfidTagId,
      description: description || undefined,
      categoryId: categoryId || undefined,
      branchId,
      currentShelfId: currentShelfId || undefined,
      rowNumber,
      errors: errors.length > 0 ? errors : undefined,
    });
  }

  return rows;
}

// Simple CSV line parser that handles quoted fields
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        current += '"';
        i++; // Skip next quote
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current);

  return result;
}

export function ItemsPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'root' || user?.role === 'admin';
  const title = useIndustryTranslation('pages.items.title');
  const description = useIndustryTranslation('pages.items.description');
  const { activeTenant } = useTenantSwitcher();

  // Industry-specific translations
  const actionCreate = useIndustryTranslation('pages.items.actions.create');
  const listEmpty = useIndustryTranslation('pages.items.list.empty');
  const listLoading = useIndustryTranslation('pages.items.list.loading');
  const successCreate = useIndustryTranslation('pages.items.success.create');
  const successDelete = useIndustryTranslation('pages.items.success.delete');
  const errorLoad = useIndustryTranslation('pages.items.error.load');
  const errorCreate = useIndustryTranslation('pages.items.error.create');
  const errorDelete = useIndustryTranslation('pages.items.error.delete');
  const dialogCreateTitle = useIndustryTranslation('pages.items.dialogs.create.title');
  const dialogCreateDescription = useIndustryTranslation('pages.items.dialogs.create.description');
  const dialogDeleteTitle = useIndustryTranslation('pages.items.dialogs.delete.title');
  const dialogUploadCsvTitle = useIndustryTranslation('pages.items.dialogs.uploadCsv.title');
  const dialogUploadCsvDescription = useIndustryTranslation('pages.items.dialogs.uploadCsv.description');
  const dialogUploadCsvError = useIndustryTranslation('pages.items.dialogs.uploadCsv.error');
  const categoryLabel = useIndustryTranslation('pages.items.form.category');
  const branchLabel = useIndustryTranslation('pages.items.form.branch');
  const selectBranchLabel = useIndustryTranslation('pages.items.form.selectBranch');
  const selectCategoryLabel = useIndustryTranslation('pages.items.form.selectCategory');
  const shelfLabel = useIndustryTranslation('pages.items.form.shelf');
  const selectShelfLabel = useIndustryTranslation('pages.items.form.selectShelf');
  const nameTableLabel = useIndustryTranslation('pages.items.table.name');
  const skuTableLabel = useIndustryTranslation('pages.items.table.sku');
  const categoryTableLabel = useIndustryTranslation('pages.items.table.category');
  const branchTableLabel = useIndustryTranslation('pages.items.table.branch');
  const shelfTableLabel = useIndustryTranslation('pages.items.table.shelf');
  const lastDetectedTableLabel = useIndustryTranslation('pages.items.table.lastDetected');
  const statusTableLabel = useIndustryTranslation('pages.items.table.status');
  const statusDetectedLabel = useIndustryTranslation('pages.items.status.detected');
  const statusNotSeenRecentlyLabel = useIndustryTranslation('pages.items.status.notSeenRecently');
  const statusMissingLabel = useIndustryTranslation('pages.items.status.missing');
  const dialogBulkTitle = useIndustryTranslation('pages.items.dialogs.bulk.title');
  const dialogBulkDescription = useIndustryTranslation('pages.items.dialogs.bulk.description');
  const actionAddMultiple = useIndustryTranslation('pages.items.actions.addMultiple');

  const [items, setItems] = useState<Item[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [shelves, setShelves] = useState<Shelf[]>([]);
  const [isLoadingShelves, setIsLoadingShelves] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isUploadCsvDialogOpen, setIsUploadCsvDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBulkDialogOpen, setIsBulkDialogOpen] = useState(false);
  const [isBulkSubmitting, setIsBulkSubmitting] = useState(false);
  const [bulkCreatedItems, setBulkCreatedItems] = useState<Item[] | null>(null);
  const [bulkCategoryId, setBulkCategoryId] = useState('');
  const [bulkBranchId, setBulkBranchId] = useState('');
  const [bulkCount, setBulkCount] = useState(1);
  const [bulkShelfId, setBulkShelfId] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [isFetchingSuggestion, setIsFetchingSuggestion] = useState(false);
  const [createCustomFieldDefinitions, setCreateCustomFieldDefinitions] = useState<
    CustomFieldDefinition[]
  >([]);
  const [createCustomFieldValues, setCreateCustomFieldValues] = useState<
    Record<string, string | number>
  >({});
  const [isLoadingCreateCustomFields, setIsLoadingCreateCustomFields] =
    useState(false);
  const [createCustomFieldsError, setCreateCustomFieldsError] = useState<
    string | null
  >(null);

  const pagination = usePaginationFromUrl();
  const [searchParams, setSearchParams] = useSearchParams();
  const filterBranchId = searchParams.get('branchId') ?? '';
  const filterCategoryId = searchParams.get('categoryId') ?? '';
  const filterShelfId = searchParams.get('shelfId') ?? '';
  const filterZone = searchParams.get('zone') ?? '';

  const setFilter = useCallback(
    (key: 'branchId' | 'categoryId' | 'shelfId' | 'zone', value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set(key, value);
          else next.delete(key);
          next.set('page', '1');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const clearFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('branchId');
        next.delete('categoryId');
        next.delete('shelfId');
        next.delete('zone');
        next.set('page', '1');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const distinctZones = useMemo(() => {
    const zones = new Set<string>();
    shelves.forEach((s) => {
      if (s.zone != null && s.zone !== '') zones.add(s.zone);
    });
    return Array.from(zones).sort();
  }, [shelves]);

  const filterShelves = useMemo(() => {
    if (!filterBranchId) return shelves;
    return shelves.filter((s) => s.branchId === filterBranchId);
  }, [shelves, filterBranchId]);

  // CSV upload state
  const [, setCsvFile] = useState<File | null>(null);
  const [parsedRows, setParsedRows] = useState<ParsedCsvRow[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadErrors, setUploadErrors] = useState<
    Array<{ row: number; error: string }>
  >([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ItemFormValues>({
    resolver: zodResolver(itemFormSchema),
    defaultValues: {
      name: '',
      sku: '',
      rfidTagId: '',
      description: '',
      categoryId: '',
      branchId: '',
      currentShelfId: '',
    },
  });

  const fetchItems = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      const result = await listItemsPaginated({
        page: pagination.page,
        limit: pagination.limit,
        ...(filterBranchId && { branchId: filterBranchId }),
        ...(filterCategoryId && { categoryId: filterCategoryId }),
        ...(filterShelfId && { currentShelfId: filterShelfId }),
        ...(filterZone && { zone: filterZone }),
      });
      setItems(result.data);
      pagination.setTotal(result.total);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : errorLoad,
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    errorLoad,
    pagination.page,
    pagination.limit,
    pagination.setTotal,
    activeTenant?.id,
    filterBranchId,
    filterCategoryId,
    filterShelfId,
    filterZone,
  ]);

  useEffect(() => {
    void fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [items, pagination.page, filterBranchId, filterCategoryId, filterShelfId, filterZone]);

  const allSelected =
    items.length > 0 && items.every((i) => selectedIds.has(i.id));
  const someSelected =
    items.length > 0 && items.some((i) => selectedIds.has(i.id));

  const toggleSelectAll = useCallback(() => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map((i) => i.id)));
    }
  }, [allSelected, items]);

  const toggleSelectItem = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleBulkDelete = useCallback(async (): Promise<void> => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setIsBulkDeleting(true);
    try {
      for (const id of ids) {
        await deleteItem(id);
      }
      toast.success(t('pages.items.bulkActions.deleteSuccess', { count: ids.length }));
      setSelectedIds(new Set());
      setIsBulkDeleteDialogOpen(false);
      await fetchItems();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t('pages.items.error.delete'),
      );
    } finally {
      setIsBulkDeleting(false);
    }
  }, [selectedIds, deleteItem, fetchItems, t]);

  const handleBulkPrintQrSelected = useCallback(() => {
    const toPrint = items.filter((i) => selectedIds.has(i.id));
    if (!toPrint.length) return;
    navigate('/items/print-qr-batch', { state: { items: toPrint } });
    setSelectedIds(new Set());
  }, [items, selectedIds, navigate]);

  // Fetch branches
  useEffect(() => {
    const loadBranches = async (): Promise<void> => {
      setIsLoadingBranches(true);
      try {
        const data = await listBranches();
        setBranches(data);
      } catch (err) {
        console.error('Failed to load branches', err);
      } finally {
        setIsLoadingBranches(false);
      }
    };
    void loadBranches();
  }, [activeTenant?.id]);

  // Fetch categories
  useEffect(() => {
    const loadCategories = async (): Promise<void> => {
      setIsLoadingCategories(true);
      try {
        const data = await listCategories();
        setCategories(data);
      } catch (err) {
        console.error('Failed to load categories', err);
      } finally {
        setIsLoadingCategories(false);
      }
    };
    void loadCategories();
  }, [activeTenant?.id]);

  // Fetch shelves
  useEffect(() => {
    const loadShelves = async (): Promise<void> => {
      setIsLoadingShelves(true);
      try {
        const data = await listShelves();
        setShelves(data);
      } catch (err) {
        console.error('Failed to load shelves', err);
      } finally {
        setIsLoadingShelves(false);
      }
    };
    void loadShelves();
  }, [activeTenant?.id]);

  // Filter shelves by selected branch
  const watchBranchId = watch('branchId');
  const watchCategoryId = watch('categoryId');
  const availableShelves = useMemo(() => {
    if (!watchBranchId) return [];
    return shelves.filter((shelf) => shelf.branchId === watchBranchId);
  }, [shelves, watchBranchId]);

  // Load custom field definitions when category changes in create dialog
  useEffect(() => {
    if (!watchCategoryId || !isCreateDialogOpen) {
      setCreateCustomFieldDefinitions([]);
      setCreateCustomFieldValues({});
      setCreateCustomFieldsError(null);
      return;
    }
    let cancelled = false;
    setIsLoadingCreateCustomFields(true);
    setCreateCustomFieldsError(null);
    const load = async (): Promise<void> => {
      try {
        const [categoryDefs, tenantDefs] = await Promise.all([
          listCustomFieldDefinitions(watchCategoryId),
          listCustomFieldDefinitions(null),
        ]);
        if (cancelled) return;
        const keySet = new Set<string>();
        const merged: CustomFieldDefinition[] = [];
        for (const d of [...categoryDefs, ...tenantDefs]) {
          if (!keySet.has(d.key)) {
            keySet.add(d.key);
            merged.push(d);
          }
        }
        setCreateCustomFieldDefinitions(merged);
        setCreateCustomFieldValues({});
      } catch (err) {
        if (!cancelled) {
          setCreateCustomFieldDefinitions([]);
          setCreateCustomFieldValues({});
          setCreateCustomFieldsError(
            err instanceof Error ? err.message : t('pages.customFields.loadError'),
          );
          toast.error(t('pages.customFields.loadError'));
        }
      } finally {
        if (!cancelled) {
          setIsLoadingCreateCustomFields(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [watchCategoryId, isCreateDialogOpen, t]);

  const availableBulkShelves = useMemo(() => {
    if (!bulkBranchId) return [];
    return shelves.filter((shelf) => shelf.branchId === bulkBranchId);
  }, [shelves, bulkBranchId]);

  const formatDate = useCallback((date: Date | string | null | undefined): string => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  const handleCreate = async (values: ItemFormValues): Promise<void> => {
    setIsSubmitting(true);
    try {
      const payload: CreateItemPayload = {
        name: values.name,
        description: values.description || undefined,
        categoryId: values.categoryId || undefined,
        branchId: values.branchId,
        currentShelfId: values.currentShelfId || undefined,
      };
      if (values.sku?.trim()) payload.sku = values.sku.trim();
      if (values.rfidTagId?.trim()) payload.rfidTagId = values.rfidTagId.trim();
      const createdItem = await createItem(payload);
      if (createCustomFieldDefinitions.length > 0) {
        const valuesToSave: Record<string, string | number> = {};
        for (const def of createCustomFieldDefinitions) {
          valuesToSave[def.key] = createCustomFieldValues[def.key] ?? '';
        }
        await upsertItemCustomFieldValues(createdItem.id, valuesToSave);
      }
      toast.success(successCreate);
      reset();
      setCreateCustomFieldDefinitions([]);
      setCreateCustomFieldValues({});
      setIsCreateDialogOpen(false);
      await fetchItems();
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : errorCreate,
      );
    } finally {
      setIsSubmitting(false);
    }
  };


  const handleDelete = async (): Promise<void> => {
    if (!selectedItem) return;
    setIsDeleting(true);
    try {
      await deleteItem(selectedItem.id);
      toast.success(successDelete);
      setIsDeleteDialogOpen(false);
      setSelectedItem(null);
      await fetchItems();
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : errorDelete,
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteDialog = useCallback((item: Item) => {
    setSelectedItem(item);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleRowClick = useCallback(
    (itemId: string) => {
      return () => navigate(`/items/${itemId}`);
    },
    [navigate],
  );

  const handleEditClick = useCallback(
    (itemId: string) => {
      return () => navigate(`/items/${itemId}/edit`);
    },
    [navigate],
  );

  const handleDeleteClick = useCallback(
    (item: Item) => {
      return () => openDeleteDialog(item);
    },
    [openDeleteDialog],
  );

  // CSV Upload handlers
  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      if (!file.name.endsWith('.csv')) {
        toast.error(t('pages.items.dialogs.uploadCsv.invalidFormat'));
        return;
      }

      setCsvFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const parsed = parseCSV(text);
          setParsedRows(parsed);
          setUploadErrors([]);
        } catch (err) {
          toast.error(
            err instanceof Error
              ? err.message
              : t('pages.items.dialogs.uploadCsv.invalidFormat'),
          );
          setCsvFile(null);
          setParsedRows([]);
        }
      };
      reader.readAsText(file);
    },
    [t],
  );

  const handleCsvUpload = async (): Promise<void> => {
    if (parsedRows.length === 0) return;

    const validRows = parsedRows.filter((row) => !row.errors);
    if (validRows.length === 0) {
      toast.error(dialogUploadCsvError);
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadErrors([]);

    const errors: Array<{ row: number; error: string }> = [];
    let successCount = 0;

    // Process in batches of 10
    const batchSize = 10;
    for (let i = 0; i < validRows.length; i += batchSize) {
      const batch = validRows.slice(i, i + batchSize);
      const batchPromises = batch.map(async (row) => {
        try {
          const payload: CreateItemPayload = {
            name: row.name,
            description: row.description,
            categoryId: row.categoryId,
            branchId: row.branchId,
            currentShelfId: row.currentShelfId,
          };
          if (row.sku) payload.sku = row.sku;
          if (row.rfidTagId) payload.rfidTagId = row.rfidTagId;
          await createItem(payload);
          successCount++;
        } catch (err) {
          errors.push({
            row: row.rowNumber,
            error:
              err instanceof Error
                ? err.message
                : errorCreate,
          });
        }
      });

      await Promise.all(batchPromises);
      setUploadProgress(((i + batch.length) / validRows.length) * 100);
    }

    setUploadErrors(errors);
    setIsUploading(false);

    if (successCount > 0) {
      // Use t() with interpolation for messages with placeholders
      const successKey = activeTenant?.industry
        ? `pages.items.dialogs.uploadCsv.success.${activeTenant.industry}`
        : 'pages.items.dialogs.uploadCsv.success.default';
      const translated = t(successKey, { count: successCount });
      // If translation exists (not same as key), use it, otherwise use default
      toast.success(
        translated !== successKey
          ? translated
          : t('pages.items.dialogs.uploadCsv.success.default', { count: successCount }),
      );
      await fetchItems();
      if (errors.length === 0) {
        setIsUploadCsvDialogOpen(false);
        setCsvFile(null);
        setParsedRows([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    }

    if (errors.length > 0) {
      toast.error(
        `${successCount} items created, ${errors.length} failed`,
      );
    }
  };

  const handleDownloadTemplate = useCallback(() => {
    const template = `name,sku,rfidTagId,description,categoryId,branchId,currentShelfId
"Product A","SKU-001","E20012345678901234567890","Description here","Electronics","branch-uuid-here"
"Product B","SKU-002","E20012345678901234567891","","Electronics","branch-uuid-here"`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'items-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  const handleBulkDialogClose = useCallback((open: boolean) => {
    if (!open) {
      setBulkCreatedItems(null);
      setBulkCategoryId('');
      setBulkBranchId('');
      setBulkCount(1);
      setBulkShelfId('');
    }
    setIsBulkDialogOpen(open);
  }, []);

  const handleBulkSubmit = useCallback(async () => {
    if (!bulkCategoryId || !bulkBranchId || bulkCount < 1) return;
    setIsBulkSubmitting(true);
    try {
      const created = await createBulkByCategory({
        categoryId: bulkCategoryId,
        branchId: bulkBranchId,
        count: bulkCount,
        currentShelfId: bulkShelfId || undefined,
      });
      setBulkCreatedItems(created);
      toast.success(t('pages.items.dialogs.bulk.success', { count: created.length }));
      await fetchItems();
    } catch {
      toast.error(t('pages.items.error.create'));
    } finally {
      setIsBulkSubmitting(false);
    }
  }, [bulkCategoryId, bulkBranchId, bulkCount, bulkShelfId, createBulkByCategory, fetchItems, t]);

  const handleBulkPrintQr = useCallback(() => {
    if (!bulkCreatedItems?.length) return;
    navigate('/items/print-qr-batch', { state: { items: bulkCreatedItems } });
    handleBulkDialogClose(false);
  }, [bulkCreatedItems, navigate, handleBulkDialogClose]);

  const validCsvRows = useMemo(
    () => parsedRows.filter((row) => !row.errors),
    [parsedRows],
  );

  const invalidCsvRows = useMemo(
    () => parsedRows.filter((row) => row.errors),
    [parsedRows],
  );

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={title}
        description={description}
        actions={
          <div className="flex gap-2">
            {isAdmin && (
            <>
            <Dialog
              open={isUploadCsvDialogOpen}
              onOpenChange={(open) => {
                setIsUploadCsvDialogOpen(open);
                if (!open) {
                  setCsvFile(null);
                  setParsedRows([]);
                  setUploadErrors([]);
                  setUploadProgress(0);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }
              }}
            >
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Upload className="mr-2 h-4 w-4" />
                  {t('pages.items.actions.uploadCsv')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {dialogUploadCsvTitle}
                  </DialogTitle>
                  <DialogDescription>
                    {dialogUploadCsvDescription}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label htmlFor="csv-file">
                        {t('pages.items.dialogs.uploadCsv.selectFile')}
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        {t('pages.items.dialogs.uploadCsv.templateDescription')}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleDownloadTemplate}
                    >
                      <Download className="mr-2 h-4 w-4" />
                      {t('pages.items.dialogs.uploadCsv.downloadTemplate')}
                    </Button>
                  </div>
                  <Input
                    id="csv-file"
                    type="file"
                    accept=".csv"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    disabled={isUploading}
                  />

                  {parsedRows.length > 0 && (
                    <div className="space-y-4">
                      <div className="rounded-lg border p-4">
                        <h4 className="mb-2 text-sm font-medium">
                          {t('pages.items.dialogs.uploadCsv.preview')} (
                          {validCsvRows.length} valid, {invalidCsvRows.length}{' '}
                          invalid)
                        </h4>
                        <div className="max-h-64 overflow-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-16">Row</TableHead>
                                <TableHead>Name</TableHead>
                                <TableHead>SKU</TableHead>
                                <TableHead>RFID Tag</TableHead>
                                <TableHead>Status</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {parsedRows.map((row) => (
                                <TableRow key={row.rowNumber}>
                                  <TableCell className="font-mono text-xs">
                                    {row.rowNumber}
                                  </TableCell>
                                  <TableCell>{row.name}</TableCell>
                                  <TableCell>{row.sku}</TableCell>
                                  <TableCell className="font-mono text-xs">
                                    {row.rfidTagId}
                                  </TableCell>
                                  <TableCell>
                                    {row.errors ? (
                                      <StatusBadge variant="danger">
                                        {row.errors.join(', ')}
                                      </StatusBadge>
                                    ) : (
                                      <StatusBadge variant="success">Valid</StatusBadge>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>

                      {isUploading && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span>
                              {t('pages.items.dialogs.uploadCsv.uploading')}
                            </span>
                            <span>{Math.round(uploadProgress)}%</span>
                          </div>
                          <Progress value={uploadProgress} />
                        </div>
                      )}

                      {uploadErrors.length > 0 && (
                        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
                          <h4 className="mb-2 text-sm font-medium text-destructive">
                            Errors ({uploadErrors.length})
                          </h4>
                          <div className="max-h-32 space-y-1 overflow-auto text-xs">
                            {uploadErrors.map((err, idx) => (
                              <div key={idx}>
                                Row {err.row}: {err.error}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsUploadCsvDialogOpen(false)}
                    disabled={isUploading}
                  >
                    {t('pages.items.actions.cancel')}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleCsvUpload}
                    disabled={
                      isUploading ||
                      parsedRows.length === 0 ||
                      validCsvRows.length === 0
                    }
                  >
                    {isUploading
                      ? t('pages.items.dialogs.uploadCsv.uploading')
                      : t('pages.items.actions.save')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog
              open={isBulkDialogOpen}
              onOpenChange={handleBulkDialogClose}
            >
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  {actionAddMultiple}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{dialogBulkTitle}</DialogTitle>
                  <DialogDescription>{dialogBulkDescription}</DialogDescription>
                </DialogHeader>
                {bulkCreatedItems == null ? (
                  <>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="bulk-branch">{branchLabel}</Label>
                        <Select
                          value={bulkBranchId || undefined}
                          onValueChange={(value) => {
                            setBulkBranchId(value || '');
                            setBulkShelfId('');
                          }}
                          disabled={isBulkSubmitting || isLoadingBranches}
                        >
                          <SelectTrigger id="bulk-branch">
                            <SelectValue placeholder={selectBranchLabel} />
                          </SelectTrigger>
                          <SelectContent>
                            {branches.map((branch) => (
                              <SelectItem key={branch.id} value={branch.id}>
                                {branch.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="bulk-category">{categoryLabel}</Label>
                        <Select
                          value={bulkCategoryId || undefined}
                          onValueChange={setBulkCategoryId}
                          disabled={isBulkSubmitting || isLoadingCategories}
                        >
                          <SelectTrigger id="bulk-category">
                            <SelectValue placeholder={selectCategoryLabel} />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map((cat) => (
                              <SelectItem key={cat.id} value={cat.id}>
                                {cat.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="bulk-count">{t('pages.items.dialogs.bulk.quantity')}</Label>
                      <Input
                        id="bulk-count"
                        type="number"
                        min={1}
                        max={1000}
                        value={bulkCount}
                        onChange={(e) => setBulkCount(Math.max(1, Math.min(1000, Number(e.target.value) || 1)))}
                        disabled={isBulkSubmitting}
                      />
                      <p className="text-xs text-muted-foreground">
                        {t('pages.items.dialogs.bulk.quantityHelp')}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="bulk-shelf">
                        {shelfLabel} ({t('pages.items.form.optional')})
                      </Label>
                      <Select
                        value={bulkShelfId || undefined}
                        onValueChange={(v) => setBulkShelfId(v || '')}
                        disabled={isBulkSubmitting || isLoadingShelves || !bulkBranchId}
                      >
                        <SelectTrigger id="bulk-shelf">
                          <SelectValue placeholder={selectShelfLabel} />
                        </SelectTrigger>
                        <SelectContent>
                          {availableBulkShelves.length === 0 ? (
                            <SelectItem value="none" disabled>
                              {bulkBranchId
                                ? t('pages.items.form.noShelves')
                                : t('pages.items.form.selectBranchFirst')}
                            </SelectItem>
                          ) : (
                            availableBulkShelves.map((shelf) => (
                              <SelectItem key={shelf.id} value={shelf.id}>
                                {formatShelfLocationLabel(shelf)}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <DialogFooter>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleBulkDialogClose(false)}
                        disabled={isBulkSubmitting}
                      >
                        {t('pages.items.actions.cancel')}
                      </Button>
                      <Button
                        type="button"
                        onClick={handleBulkSubmit}
                        disabled={isBulkSubmitting || !bulkCategoryId || !bulkBranchId}
                      >
                        {isBulkSubmitting ? t('pages.items.actions.saving') : t('pages.items.actions.save')}
                      </Button>
                    </DialogFooter>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground">
                      {t('pages.items.dialogs.bulk.success', { count: bulkCreatedItems.length })}
                    </p>
                    <DialogFooter>
                      <Button type="button" onClick={handleBulkPrintQr}>
                        {t('pages.items.dialogs.bulk.printQr')}
                      </Button>
                      <Button variant="outline" onClick={() => handleBulkDialogClose(false)}>
                        {t('pages.items.actions.cancel')}
                      </Button>
                    </DialogFooter>
                  </>
                )}
              </DialogContent>
            </Dialog>

            <Dialog
              open={isCreateDialogOpen}
              onOpenChange={(open) => {
                setIsCreateDialogOpen(open);
                if (!open) {
                  setIsFetchingSuggestion(false);
                  reset();
                  setCreateCustomFieldDefinitions([]);
                  setCreateCustomFieldValues({});
                  setIsLoadingCreateCustomFields(false);
                  setCreateCustomFieldsError(null);
                }
              }}
            >
              <DialogTrigger asChild>
                <Button size="sm">
                  {actionCreate}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {dialogCreateTitle}
                  </DialogTitle>
                  <DialogDescription>
                    {dialogCreateDescription}
                  </DialogDescription>
                </DialogHeader>
                <form
                  id="create-item-form"
                  className="space-y-4"
                  onSubmit={handleSubmit(handleCreate)}
                >
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="create-branch">
                        {branchLabel} *
                      </Label>
                      <Controller
                        name="branchId"
                        control={control}
                        render={({ field }) => (
                          <Select
                            value={field.value}
                            onValueChange={(value) => {
                              field.onChange(value);
                              // Reset shelf when branch changes
                              setValue('currentShelfId', '');
                            }}
                            disabled={isSubmitting || isLoadingBranches}
                          >
                            <SelectTrigger id="create-branch">
                              <SelectValue placeholder={selectBranchLabel} />
                            </SelectTrigger>
                            <SelectContent>
                              {branches.map((branch) => (
                                <SelectItem key={branch.id} value={branch.id}>
                                  {branch.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                      {errors.branchId && (
                        <p className="text-xs text-destructive">
                          {t(errors.branchId.message as string)}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="create-category">
                        {categoryLabel}
                      </Label>
                      <Controller
                        name="categoryId"
                        control={control}
                        render={({ field }) => (
                          <Select
                            value={field.value || undefined}
                            onValueChange={async (value) => {
                              field.onChange(value || undefined);
                              if (value) {
                                setIsFetchingSuggestion(true);
                                try {
                                  const suggestion = await getNextProductSuggestion(value);
                                  setValue('name', suggestion.suggestedName);
                                  setValue('sku', suggestion.suggestedSku);
                                } catch {
                                  // leave name/sku as-is on error
                                } finally {
                                  setIsFetchingSuggestion(false);
                                }
                              } else {
                                setValue('name', '');
                                setValue('sku', '');
                              }
                            }}
                            disabled={isSubmitting || isLoadingCategories}
                          >
                            <SelectTrigger id="create-category">
                              <SelectValue placeholder={selectCategoryLabel} />
                            </SelectTrigger>
                            <SelectContent>
                              {categories.map((cat) => (
                                <SelectItem key={cat.id} value={cat.id}>
                                  {cat.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="create-name">
                      {t('pages.items.form.name')}
                    </Label>
                    <div className="relative">
                      <Input
                        id="create-name"
                        {...register('name')}
                        disabled={isSubmitting || isFetchingSuggestion}
                        className={isFetchingSuggestion ? 'pr-9' : ''}
                      />
                      {isFetchingSuggestion && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
                          <Loader2 className="h-4 w-4 animate-spin" />
                        </span>
                      )}
                    </div>
                    {errors.name && (
                      <p className="text-xs text-destructive">
                        {t(errors.name.message as string)}
                      </p>
                    )}
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="create-sku">
                        {t('pages.items.form.sku')} *
                      </Label>
                      <Input
                        id="create-sku"
                        {...register('sku')}
                        placeholder={t('pages.items.form.skuAutoGenerate')}
                        disabled={isSubmitting}
                      />
                      {errors.sku && (
                        <p className="text-xs text-destructive">
                          {t(errors.sku.message as string)}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="create-rfidTag">
                        {t('pages.items.form.rfidTag')} ({t('pages.items.form.optional')})
                      </Label>
                      <Input
                        id="create-rfidTag"
                        {...register('rfidTagId')}
                        placeholder={t('pages.items.form.rfidTagOptional')}
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="create-description">
                      {t('pages.items.form.description')}
                    </Label>
                    <Input
                      id="create-description"
                      {...register('description')}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="create-shelf">
                      {shelfLabel} ({t('pages.items.form.optional')})
                    </Label>
                    <Controller
                      name="currentShelfId"
                      control={control}
                      render={({ field }) => (
                        <Select
                          value={field.value || undefined}
                          onValueChange={(value) => field.onChange(value || undefined)}
                          disabled={isSubmitting || isLoadingShelves || !watchBranchId}
                        >
                          <SelectTrigger id="create-shelf">
                            <SelectValue placeholder={selectShelfLabel} />
                          </SelectTrigger>
                          <SelectContent>
                            {availableShelves.length === 0 ? (
                              <SelectItem value="none" disabled>
                                {watchBranchId
                                  ? t('pages.items.form.noShelves')
                                  : t('pages.items.form.selectBranchFirst')}
                              </SelectItem>
                            ) : (
                              availableShelves.map((shelf) => (
                                <SelectItem key={shelf.id} value={shelf.id}>
                                  {formatShelfLocationLabel(shelf)}
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                  {(watchCategoryId && isLoadingCreateCustomFields && (
                    <div className="border-t pt-4 flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                      {t('pages.customFields.loading')}
                    </div>
                  )) ||
                  (watchCategoryId && createCustomFieldsError && (
                    <div className="border-t pt-4 text-sm text-destructive">
                      {createCustomFieldsError}
                    </div>
                  )) ||
                  (createCustomFieldDefinitions.length > 0 && (
                    <div className="space-y-4 border-t pt-4">
                      <p className="text-sm font-medium">
                        {t('pages.customFields.title')}
                      </p>
                      <div className="space-y-4">
                        {createCustomFieldDefinitions.map((def) => (
                          <div key={def.id} className="space-y-2">
                            <Label htmlFor={`create-cf-${def.key}`}>
                              {def.name}
                              {def.required && ' *'}
                            </Label>
                            {def.type === 'textarea' ? (
                              <textarea
                                id={`create-cf-${def.key}`}
                                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                value={String(createCustomFieldValues[def.key] ?? '')}
                                onChange={(e) =>
                                  setCreateCustomFieldValues((prev) => ({
                                    ...prev,
                                    [def.key]: e.target.value,
                                  }))
                                }
                                disabled={isSubmitting}
                              />
                            ) : def.type === 'options' && def.options?.length ? (
                              <Select
                                value={String(createCustomFieldValues[def.key] ?? '')}
                                onValueChange={(v) =>
                                  setCreateCustomFieldValues((prev) => ({
                                    ...prev,
                                    [def.key]: v,
                                  }))
                                }
                                disabled={isSubmitting}
                              >
                                <SelectTrigger id={`create-cf-${def.key}`}>
                                  <SelectValue
                                    placeholder={t('pages.customFields.form.selectOption')}
                                  />
                                </SelectTrigger>
                                <SelectContent>
                                  {def.options.map((opt) => (
                                    <SelectItem key={opt} value={opt}>
                                      {opt}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <Input
                                id={`create-cf-${def.key}`}
                                type={
                                  def.type === 'number'
                                    ? 'number'
                                    : def.type === 'url'
                                      ? 'url'
                                      : 'text'
                                }
                                value={String(createCustomFieldValues[def.key] ?? '')}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setCreateCustomFieldValues((prev) => ({
                                    ...prev,
                                    [def.key]:
                                      def.type === 'number'
                                        ? val === ''
                                          ? ''
                                          : Number(val)
                                        : val,
                                  }));
                                }}
                                disabled={isSubmitting}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </form>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsFetchingSuggestion(false);
                      reset();
                      setIsCreateDialogOpen(false);
                    }}
                    disabled={isSubmitting}
                  >
                    {t('pages.items.actions.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    form="create-item-form"
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? t('pages.items.actions.saving')
                      : t('pages.items.actions.save')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            </>
            )}
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={filterBranchId || 'all'}
          onValueChange={(v) => setFilter('branchId', v === 'all' ? '' : v)}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder={t('pages.items.filters.allBranches')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('pages.items.filters.allBranches')}</SelectItem>
            {branches.map((b) => (
              <SelectItem key={b.id} value={b.id}>
                {b.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filterCategoryId || 'all'}
          onValueChange={(v) => setFilter('categoryId', v === 'all' ? '' : v)}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder={t('pages.items.filters.allCategories')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('pages.items.filters.allCategories')}</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filterShelfId || 'all'}
          onValueChange={(v) => setFilter('shelfId', v === 'all' ? '' : v)}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder={t('pages.items.filters.allShelves')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('pages.items.filters.allShelves')}</SelectItem>
            {filterShelves.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {formatShelfLocationLabel(s)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filterZone || 'all'}
          onValueChange={(v) => setFilter('zone', v === 'all' ? '' : v)}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder={t('pages.items.filters.allZones')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('pages.items.filters.allZones')}</SelectItem>
            {distinctZones.map((z) => (
              <SelectItem key={z} value={z}>
                {z}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(filterBranchId || filterCategoryId || filterShelfId || filterZone) && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            {t('pages.items.filters.clearFilters')}
          </Button>
        )}
      </div>

      {selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/50 px-3 py-2">
          <span className="text-sm text-muted-foreground">
            {t('pages.items.bulkActions.selectedCount', { count: selectedIds.size })}
          </span>
          {isAdmin && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setIsBulkDeleteDialogOpen(true)}
            >
              {t('pages.items.bulkActions.deleteSelected')}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleBulkPrintQrSelected}
          >
            {t('pages.items.bulkActions.printQrSelected')}
          </Button>
        </div>
      )}

      <TableCard>
            {isLoading ? (
              <div className="flex flex-col items-center gap-3 py-8">
                <LoadingLogo size="sm" />
                <span className="text-sm text-muted-foreground">{listLoading}</span>
              </div>
            ) : items.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                {listEmpty}
              </div>
        ) : (
          <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    aria-label={t('pages.items.bulkActions.selectAllOnPage')}
                    checked={
                      allSelected ? true : someSelected ? 'indeterminate' : false
                    }
                    onCheckedChange={toggleSelectAll}
                    onClick={(e) => e.stopPropagation()}
                  />
                </TableHead>
                <TableHead>{nameTableLabel}</TableHead>
                <TableHead>{skuTableLabel}</TableHead>
                <TableHead>{categoryTableLabel}</TableHead>
                <TableHead>{branchTableLabel}</TableHead>
                <TableHead>{shelfTableLabel}</TableHead>
                <TableHead>{statusTableLabel}</TableHead>
                <TableHead>{lastDetectedTableLabel}</TableHead>
                <TableHead className="w-[70px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => {
                const actions: TableAction[] = isAdmin
                  ? [
                      {
                        label: t('pages.items.actions.edit'),
                        icon: Pencil,
                        onClick: handleEditClick(item.id),
                      },
                      {
                        label: t('pages.items.actions.delete'),
                        icon: Trash2,
                        onClick: handleDeleteClick(item),
                        variant: 'destructive',
                      },
                    ]
                  : [];
                const detectionStatus = getItemDetectionStatus(item);
                const statusLabel =
                  detectionStatus === 'detected'
                    ? statusDetectedLabel
                    : detectionStatus === 'not_seen_recently'
                      ? statusNotSeenRecentlyLabel
                      : statusMissingLabel;
                return (
                  <TableRow
                    key={item.id}
                    onRowClick={handleRowClick(item.id)}
                  >
                  <TableCell
                    className="w-10"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      aria-label={t('pages.items.bulkActions.selectItem', {
                        name: item.name,
                      })}
                      checked={selectedIds.has(item.id)}
                      onCheckedChange={() => toggleSelectItem(item.id)}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell className="font-mono text-sm">
                    {item.sku}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {categories.find((c) => c.id === item.categoryId)?.name || '-'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {branches.find((b) => b.id === item.branchId)?.name || '-'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {shelves.find((s) => s.id === item.currentShelfId)?.name || '-'}
                  </TableCell>
                  <TableCell>
                    <ProductStatusBadge status={detectionStatus} label={statusLabel} />
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {formatDate(item.lastDetectedAt)}
                  </TableCell>
                  <TableCell>
                    <TableActionsMenu actions={actions} />
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>

            <TablePagination
              pagination={pagination}
              rowsPerPageLabel={t('pages.common.pagination.rowsPerPage')}
              pageOfLabel={t('pages.common.pagination.pageOf', {
                current: pagination.page,
                total: pagination.totalPages,
              })}
              onRefresh={() => void fetchItems()}
              isRefreshing={isLoading}
            />
          </>
        )}
      </TableCard>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        title={dialogDeleteTitle}
        description={t('pages.items.dialogs.delete.description', {
          name: selectedItem?.name ?? '',
        })}
        confirmText={t('pages.items.actions.delete')}
        cancelText={t('pages.items.actions.cancel')}
        variant="destructive"
        isLoading={isDeleting}
        onConfirm={handleDelete}
      />

      {/* Bulk Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={isBulkDeleteDialogOpen}
        onOpenChange={setIsBulkDeleteDialogOpen}
        title={t('pages.items.dialogs.bulkDelete.title')}
        description={t('pages.items.dialogs.bulkDelete.description', {
          count: selectedIds.size,
        })}
        confirmText={t('pages.items.actions.delete')}
        loadingText={t('pages.items.dialogs.bulkDelete.deleting')}
        cancelText={t('pages.items.actions.cancel')}
        variant="destructive"
        isLoading={isBulkDeleting}
        onConfirm={handleBulkDelete}
      />

    </div>
  );
}
